import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInviteInfo, acceptInvite } from "@/services/teamService";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Users, Loader2, CheckCircle2, Film, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await getInviteInfo(token);
        setInfo(res);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invitation");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchInfo();
  }, [token]);

  const handleAccept = async () => {
    if (!user) {
      // Store token and redirect to login
      sessionStorage.setItem("pending_invite", token);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      window.location.href = `${backendUrl}/api/auth/google/login`;
      return;
    }

    setAccepting(true);
    try {
      const res = await acceptInvite(token);
      toast.success(res.message);
      navigate(`/projects/${res.project_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="animate-spin text-[var(--color-gold-400)]" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="max-w-sm text-center">
          <XCircle size={48} className="mx-auto mb-4 text-red-400" />
          <h2 className="text-lg font-semibold text-[#fafafa] mb-2">Invitation Error</h2>
          <p className="text-sm text-[#a1a1aa] mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="btn-primary" data-testid="go-home-btn">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (info?.status === "active") {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="max-w-sm text-center">
          <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-400" />
          <h2 className="text-lg font-semibold text-[#fafafa] mb-2">Already Accepted</h2>
          <p className="text-sm text-[#a1a1aa] mb-6">You're already a member of this project.</p>
          <Button onClick={() => navigate(`/projects/${info.project_id}`)} className="btn-primary" data-testid="go-project-btn">Open Project</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center" data-testid="accept-invite-page">
      <div className="max-w-md w-full mx-4">
        <div className="bg-[#0a0a0b] border border-[#27272a] rounded-2xl overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#d4a84b]/10 flex items-center justify-center mx-auto mb-5">
              <Film size={28} className="text-[#d4a84b]" />
            </div>
            <h1 className="text-xl font-bold text-[#fafafa] mb-1">Project Invitation</h1>
            <p className="text-sm text-[#71717a]">You've been invited to collaborate</p>
          </div>

          <div className="px-8 pb-6 space-y-4">
            <div className="bg-[#18181b] rounded-xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#71717a]">Project</span>
                <span className="text-sm font-medium text-[#fafafa]">{info?.project_name}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#71717a]">Your Role</span>
                <span className="text-sm font-medium text-[#d4a84b]">{info?.role_label}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#71717a]">Invited by</span>
                <span className="text-sm text-[#d4d4d8]">{info?.inviter_name}</span>
              </div>
            </div>

            {user ? (
              <Button
                onClick={handleAccept}
                disabled={accepting}
                className="btn-primary w-full h-11 text-sm"
                data-testid="accept-invite-btn"
              >
                {accepting ? <Loader2 size={16} className="animate-spin mr-2" /> : <Users size={16} className="mr-2" />}
                Accept & Join Project
              </Button>
            ) : (
              <Button
                onClick={handleAccept}
                className="btn-primary w-full h-11 text-sm"
                data-testid="signin-to-accept-btn"
              >
                Sign In to Accept Invitation
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
